import React from "react";
import { useSearchParams } from "react-router";

import { useNavigation } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { PlusIcon } from "lucide-react";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import { useNotificationRulesColumns } from "@/pages/tina/notification-rules/hooks/use-notification-rules-columns";
import { useTagRulesColumns } from "@/pages/tina/tag-rules/hooks/use-tag-rules-columns";
import type { NotificationRuleRead, TagRuleRead } from "@/types/tina";

/** The two rule families this page owns, keyed by their Refine resource. */
type RulesTab = "tag" | "notification";

const TABS: ReadonlyArray<{
  value: RulesTab;
  label: string;
  resource: string;
  hint: string;
}> = [
  {
    value: "tag",
    label: "Tag Rules",
    resource: "tina-tag-rules",
    hint: "Curator guidance injected into the type/genre prompt — when a tag should and should not be applied.",
  },
  {
    value: "notification",
    label: "Notification Rules",
    resource: "tina-notification-rules",
    hint: "Curator guidance for the push-notification judge — which verdicts a created event earns.",
  },
];

function isRulesTab(value: string | null): value is RulesTab {
  return TABS.some((entry) => entry.value === value);
}

/**
 * Tag rules and notification rules on one page, one tab each.
 *
 * They were two sidebar entries with identical list pages (a table plus a
 * "New rule" button) over two tables of the same shape: a curator-authored
 * instruction that is injected into one LLM prompt. Splitting them cost a
 * sidebar slot each and taught nothing, so they share a page.
 *
 * The tab is persisted like the Runs page's, and an initial `?tab=` wins over
 * the persisted value so the rule-edit screens can send a curator back to the
 * tab they came from.
 */
export const RulesList = () => {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("tab");

  const [tab, setTab] = usePersistedState<RulesTab>("tina-rules:tab", "tag");

  // A `?tab=` on the URL is a one-shot instruction, not state: apply it once
  // per distinct value so a curator can still click away from it afterwards.
  const appliedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!isRulesTab(requested) || appliedRef.current === requested) return;
    appliedRef.current = requested;
    setTab(requested);
  }, [requested, setTab]);

  const { create } = useNavigation();
  const active = TABS.find((entry) => entry.value === tab) ?? TABS[0];

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Rules</h1>
        <Button onClick={() => create(active.resource)}>
          <PlusIcon className="h-4 w-4 mr-1" />
          New rule
        </Button>
      </div>
      <div className="flex flex-col gap-1">
        <Tabs value={tab} onValueChange={(value) => setTab(value as RulesTab)}>
          <TabsList>
            {TABS.map((entry) => (
              <TabsTrigger key={entry.value} value={entry.value}>
                {entry.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <p className="text-xs text-muted-foreground">{active.hint}</p>
      </div>
      {/* One table per tab, mounted only while its tab is active: `useTable`
          is a hook, so the two cannot be selected inside a single component
          without fetching both lists on every render. */}
      {tab === "tag" ? <TagRulesTable /> : <NotificationRulesTable />}
    </div>
  );
};

const TagRulesTable = () => {
  const columns = useTagRulesColumns();
  const { edit } = useNavigation();

  const table = useTable<TagRuleRead>({
    columns,
    refineCoreProps: {
      resource: "tina-tag-rules",
      pagination: { pageSize: 25 },
    },
    meta: {
      onDoubleClick: (_e: React.MouseEvent, row: { original: TagRuleRead }) => {
        edit("tina-tag-rules", row.original.id);
      },
    },
  });

  return <DataTable table={table} />;
};

const NotificationRulesTable = () => {
  const columns = useNotificationRulesColumns();
  const { edit } = useNavigation();

  const table = useTable<NotificationRuleRead>({
    columns,
    refineCoreProps: {
      resource: "tina-notification-rules",
      pagination: { pageSize: 25 },
    },
    meta: {
      onDoubleClick: (
        _e: React.MouseEvent,
        row: { original: NotificationRuleRead },
      ) => {
        edit("tina-notification-rules", row.original.id);
      },
    },
  });

  return <DataTable table={table} />;
};
